import {
  $convertFromMarkdownString,
  MultilineElementTransformer,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
} from "@lexical/markdown"
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table"
import { $createParagraphNode } from "lexical"

import { ObjectLinkTransformer } from "./object-link"

const TABLE_CELL_TRANSFORMERS = [...TEXT_FORMAT_TRANSFORMERS, ...TEXT_MATCH_TRANSFORMERS, ObjectLinkTransformer]

function appendCellContent(cellNode: TableCellNode, markdown: string) {
  if (!markdown) {
    cellNode.append($createParagraphNode())
    return
  }

  $convertFromMarkdownString(markdown, TABLE_CELL_TRANSFORMERS, cellNode)

  if (cellNode.getChildrenSize() === 0) {
    cellNode.append($createParagraphNode())
  }
}

function parseTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim())
}

function isAlignmentRow(cells: string[], columnCount: number): boolean {
  if (cells.length !== columnCount) return false
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell))
}

export const TableTransformer: MultilineElementTransformer = {
  type: "multiline-element",
  /* eslint-disable @typescript-eslint/no-explicit-any */
  dependencies: [TableNode as unknown as any, TableRowNode as unknown as any, TableCellNode as unknown as any],
  /* eslint-enable @typescript-eslint/no-explicit-any */

  regExpStart: /^\s*\|.+\|\s*$/,
  handleImportAfterStartMatch({ lines, startLineIndex, rootNode }) {
    const tableLines: string[] = []
    let current = startLineIndex

    while (current < lines.length) {
      const line = lines[current]
      if (!line.trim().startsWith("|")) break
      tableLines.push(line)
      current++
    }

    if (tableLines.length < 2) {
      return [false, startLineIndex]
    }

    const headerCells = parseTableRow(tableLines[0])
    const alignmentCells = parseTableRow(tableLines[1])

    if (!isAlignmentRow(alignmentCells, headerCells.length)) {
      return [false, startLineIndex]
    }

    const tableNode = $createTableNode()

    const headerRowNode = $createTableRowNode()
    headerCells.forEach((text) => {
      const cellNode = $createTableCellNode(TableCellHeaderStates.BOTH, 1, undefined)
      appendCellContent(cellNode, text)
      headerRowNode.append(cellNode)
    })
    tableNode.append(headerRowNode)

    for (let i = 2; i < tableLines.length; i++) {
      const rowCells = parseTableRow(tableLines[i])
      if (rowCells.length === 1 && rowCells[0] === "") continue
      const rowNode = $createTableRowNode()
      const cells = [...rowCells]
      while (cells.length < headerCells.length) {
        cells.push("")
      }
      cells.forEach((text) => {
        const cell = $createTableCellNode()
        appendCellContent(cell, text)
        rowNode.append(cell)
      })
      tableNode.append(rowNode)
    }

    rootNode.append(tableNode)

    return [true, current - 1]
  },

  export(node) {
    if (!$isTableNode(node)) return null
    const tableNode = node as TableNode

    const rows: string[] = []
    const childRows = tableNode.getChildren()
    if (childRows.length === 0) return null

    const headerRow = childRows[0] as TableRowNode
    const headerCells = headerRow.getChildren().map((cell) => (cell as TableCellNode).getTextContent())
    rows.push(`| ${headerCells.join(" | ")} |`)
    rows.push(`|${headerCells.map(() => " --- ").join("|")}|`)

    for (let i = 1; i < childRows.length; i++) {
      const rowNode = childRows[i] as TableRowNode
      const cells = rowNode.getChildren().map((cell) => (cell as TableCellNode).getTextContent())
      rows.push(`| ${cells.join(" | ")} |`)
    }

    return rows.join("\n")
  },

  replace() {
    return false
  },
}

function $isTableNode(node: unknown): boolean {
  return (node as TableNode | undefined)?.getType?.() === "table"
}
